import type { DiscoveryPreview, SuburbInput } from "@repo/adapter-core";
import { slugify } from "@repo/adapter-core";
import type { SoldOnlyAdapterConfig } from "../shared/sold-only.ts";

// Barry Plant's sold search (/sold-properties/?suburb=…) renders only the 12 newest sales and
// ignores every page parameter tried (?page=, &pg=, &p=, /page/N/; 2026-10-14). Its sold sitemap
// lists every sale of the last 12 months in one file (10,869 on 2026-10-14; ~2 MB, generated per
// request), and each property URL ends "-<suburb>-<state>-<postcode>-<id>/". Discovery reads the
// sitemap with the task's suburb in the query (the site ignores it; the parser filters by it), so
// a suburb's task keeps its own sales. There is no page 2: page 2+ points at the small sitemap
// index (533 bytes, HTTP 200), which the parser reads as the end, so a task downloads the large
// file once.
const SOLD_SITEMAP = "https://www.barryplant.com.au/sitemap-sold-listings.xml";
const SITEMAP_INDEX = "https://www.barryplant.com.au/sitemap.xml";

export function buildSearchUrl(suburb: SuburbInput, _config: SoldOnlyAdapterConfig): string {
  const key = `${slugify(suburb.locality)}-${suburb.state.toLowerCase()}-${suburb.postcode}`;
  return `${SOLD_SITEMAP}?suburb=${key}`;
}

export function buildPaginatedUrl(searchUrl: string, page: number): string {
  if (page <= 1) return searchUrl;
  const url = new URL(searchUrl);
  url.searchParams.set("p", String(page));
  return `${SITEMAP_INDEX}${url.search}`;
}

/**
 * Page 1 of every suburb's task is the same sitemap: one stored copy serves them all. Fetched once
 * per suburb, 192 tasks of run d91e722b downloaded ~2 MB each and 35 ended blocked.
 */
export function barryPlantDiscoveryCacheKey(pageUrl: string): string | null {
  return pageUrl.startsWith(`${SOLD_SITEMAP}?`) ? SOLD_SITEMAP : null;
}

/** Sitemap entries for the page URL's suburb; [] past page 1; null when the body is no sitemap. */
export function parseBarryPlantDiscoveryPreviews(body: string, pageUrl: string): DiscoveryPreview[] | null {
  const params = new URL(pageUrl).searchParams;
  if (Number(params.get("p") ?? "1") > 1) return body.includes("<sitemapindex") ? [] : null;
  if (!body.includes("<urlset")) return null;
  const suburbKey = params.get("suburb");
  const cards: DiscoveryPreview[] = [];
  for (const match of body.matchAll(/<loc>([^<]+)<\/loc>/g)) {
    const url = match[1].trim();
    if (!suburbKey || url.includes(`-${suburbKey}-`)) cards.push({ url });
  }
  return cards;
}
